import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ContactSection({ content }) {
    const { t, lang } = useI18n();
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const hours = lang === "es" ? content?.hours_es : content?.hours_en;
    const phone = content?.phone || "+1 (972) 710-3322";
    const phone2 = content?.phone2 || "";
    const address = content?.address || "Dallas, TX";

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error(lang === "es" ? "Completa los campos requeridos" : "Please fill required fields");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success(t("form_thanks"));
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full bg-black/40 border border-gold/25 rounded-md px-4 py-3 text-habana-cream placeholder:text-habana-cream/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

    return (
        <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-habana-surface">
            <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-5"
                >
                    <div className="ornament">{t("contact_ornament")}</div>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl text-habana-cream leading-[1.05]">
                        {t("contact_title")}
                    </h2>
                    <p className="mt-4 text-habana-cream/65 max-w-md">{t("contact_sub")}</p>

                    <div className="mt-10 space-y-6">
                        <InfoRow
                            icon={<Phone size={16} />}
                            label={t("phone_label")}
                            value={
                                <div className="flex flex-col gap-0.5">
                                    <a
                                        href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                                        className="text-habana-cream hover:text-gold transition-colors"
                                        data-testid="contact-phone-link"
                                    >
                                        {phone}
                                    </a>
                                    {phone2 && (
                                        <a
                                            href={`tel:${phone2.replace(/[^+\d]/g, "")}`}
                                            className="text-habana-cream hover:text-gold transition-colors"
                                            data-testid="contact-phone2-link"
                                        >
                                            {phone2}
                                        </a>
                                    )}
                                </div>
                            }
                        />
                        <InfoRow
                            icon={<MapPin size={16} />}
                            label={t("address_label")}
                            value={<span className="text-habana-cream">{address}</span>}
                        />
                        <InfoRow
                            icon={<Clock size={16} />}
                            label={t("hours_label")}
                            value={<span className="text-habana-cream">{hours}</span>}
                        />
                    </div>

                    <div className="mt-8 flex items-center gap-4">
                        {content?.instagram && (
                            <a
                                href={content.instagram}
                                target="_blank"
                                rel="noreferrer"
                                className="text-habana-cream/70 hover:text-gold transition-colors"
                                data-testid="social-instagram"
                                aria-label="Instagram"
                            >
                                <Instagram size={20} />
                            </a>
                        )}
                        {content?.facebook && (
                            <a
                                href={content.facebook}
                                target="_blank"
                                rel="noreferrer"
                                className="text-habana-cream/70 hover:text-gold transition-colors"
                                data-testid="social-facebook"
                                aria-label="Facebook"
                            >
                                <Facebook size={20} />
                            </a>
                        )}
                    </div>
                </motion.div>

                <motion.form
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    onSubmit={submit}
                    className="lg:col-span-7 bg-black/40 border border-gold/15 rounded-2xl p-6 md:p-10"
                    data-testid="contact-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="text-xs tracking-widest uppercase text-gold/80">
                                {t("form_name")} *
                            </label>
                            <input
                                type="text"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className={`mt-2 ${inputCls}`}
                                data-testid="contact-name-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs tracking-widest uppercase text-gold/80">
                                {t("form_email")} *
                            </label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className={`mt-2 ${inputCls}`}
                                data-testid="contact-email-input"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs tracking-widest uppercase text-gold/80">
                                {t("form_phone")}
                            </label>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className={`mt-2 ${inputCls}`}
                                data-testid="contact-phone-input"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs tracking-widest uppercase text-gold/80">
                                {t("form_message")} *
                            </label>
                            <textarea
                                required
                                rows={5}
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                className={`mt-2 ${inputCls} resize-none`}
                                data-testid="contact-message-input"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-gold mt-6 w-full md:w-auto disabled:opacity-60"
                        data-testid="contact-submit-btn"
                    >
                        {submitting ? "…" : t("form_send")}
                    </button>
                </motion.form>
            </div>
        </section>
    );
}

function InfoRow({ icon, label, value }) {
    return (
        <div className="flex items-start gap-4">
            <div className="mt-1 h-8 w-8 flex items-center justify-center rounded-full border border-gold/30 text-gold">
                {icon}
            </div>
            <div>
                <div className="text-[0.65rem] tracking-[0.32em] uppercase text-habana-cream/50">
                    {label}
                </div>
                <div className="mt-0.5 font-serif text-lg">{value}</div>
            </div>
        </div>
    );
}
